import { isFalsy } from 'utility-types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { ListPageModel } from '@/interfaces/service/api.interface';
import type {
  AppStore,
  AppStoreDto,
  AppStorePostFilter,
  AppStorePostSortOption,
  Post,
  PostStatisitcs,
  PostWordData,
  PostsCountsByDurations,
  PostsCountsByGroup
} from '@/interfaces/service/appstore/appstore.interface';
import { POST_PAGGE_SIZE } from '@/constants/appstore/post.constants';
import { setBasePropsAppStore } from '@/utils/appstore.util';

interface AppStoreState {
  appStores: Record<number, AppStore>;
  onCrawlingAppStoreIds: number[];
}

export const baseAppStorePostFilter: AppStorePostFilter = {
  replyFilter: 'ALL',
  violationList: undefined,
  positiveWordList: undefined,
  negativeWordList: undefined,
  channelFilter: undefined,
  reviewType: undefined,
  hasProduct: undefined
};

interface AppStoreStoreActions {
  setAppStores: (appStores: AppStore[] | AppStoreDto[], appStoreId?: number) => void;
  updateAppStores: (appStore: AppStore | AppStoreDto) => void;
  setAppStorePosts: (appStoreId: number, posts: Post[], pageData: ListPageModel, totalCount: number) => void;
  setAppStorePostsFilters: (appStoreId: number, filter: AppStorePostFilter) => void;
  setAppStorePostsSort: (appStoreId: number, sortOption: AppStorePostSortOption) => void;
  setAppStorePostConditions: (appStoreId: number, filter: AppStorePostFilter, sortOption: AppStorePostSortOption) => void;
  updatePostStatisitcs: (appStoreId: number, statisticsKey: PostStatisitcs, data: PostsCountsByGroup | PostWordData | PostsCountsByDurations[]) => void;
  pushOnCrawlingAppStoreId: (appStoreId: number) => void;
  popOnCrawlingAppStoreId: (appStoreId: number) => void;
  clearTempAppStore: () => void;
}

type AppStoreStore = AppStoreState & AppStoreStoreActions;

export const useAppStoreStore = create(
  persist<AppStoreStore>(
    (set) => ({
      appStores: {},
      onCrawlingAppStoreIds: [],
      setAppStores: (appStores: AppStore[] | AppStoreDto[], appStoreId?: number) =>
        set((state) => {
          if (appStoreId === undefined) {
            appStoreId = -1;
          }
          let _appStores: Record<number, AppStore> = state.appStores ?? {};

          if (!isFalsy(appStoreId) && appStoreId > -1) {
            const _currentAppStore = _appStores[appStoreId] ?? {};

            // 현재 시스템상으로는 한 유저가 복수의 appStore를 등록할 수 있으나 appStore 를 선택하는 화면이 없어
            // 임시조치로 하나의 appStore만 브라우저에 저장하도록 수정
            return {
              ...state,
              appStores: {
                [appStoreId]: {
                  ..._currentAppStore,
                  ...appStores.find((newAppStore) => newAppStore.storeId === appStoreId)
                }
              }
            };
          }

          appStores.forEach((appStore: AppStore | AppStoreDto) => {
            const filter = state.appStores[appStore.storeId]?.filter;

            if (!isFalsy(filter) && isFalsy((appStore as AppStore).filter)) {
              appStore = {
                ...appStore,
                filter
              };
            } else if (isFalsy((appStore as AppStore).filter)) {
              appStore = setBasePropsAppStore(appStore);
            }

            _appStores = {
              ..._appStores,
              [appStore.storeId]: appStore as AppStore
            };
          });

          return {
            ...state,
            appStores: _appStores
          };
        }),
      updateAppStores: (appStore: AppStore | AppStoreDto) =>
        set((state) => {
          const _appStores = state.appStores;

          const targetAppStoreId = appStore.storeId;
          const _targerAppStore = _appStores[targetAppStoreId];

          if (!isFalsy(_targerAppStore)) {
            return {
              ...state,
              appStores: {
                ..._appStores,
                [targetAppStoreId]: {
                  ..._targerAppStore,
                  ...appStore
                }
              }
            };
          } else {
            return {
              ...state,
              appStores: {
                ..._appStores,
                [targetAppStoreId]: setBasePropsAppStore(appStore)
              }
            };
          }
        }),
      setAppStorePosts: (appStoreId, newPosts, pageData, totalCount) =>
        set((state) => {
          const targetAppStore = state.appStores[appStoreId];

          if (isFalsy(targetAppStore)) {
            return state;
          }

          const { postData, ...others } = targetAppStore;

          if (isFalsy(targetAppStore.postData)) {
            return {
              ...state,
              appStores: {
                ...state.appStores,
                [targetAppStore.storeId]: {
                  ...others,
                  postData: {
                    posts: newPosts,
                    pageData,
                    totalCount
                  }
                }
              }
            };
          }

          const { posts, ...postMetaData } = targetAppStore.postData;

          return {
            ...state,
            appStores: {
              ...state.appStores,
              [targetAppStore.storeId]: {
                ...others,
                postData: {
                  ...postMetaData,
                  posts: newPosts,
                  pageData,
                  totalCount
                }
              }
            }
          };
        }),
      setAppStorePostsFilters: (appStoreId, filter) =>
        set((state) => {
          let targetAppStore = state.appStores[appStoreId];
          if (isFalsy(targetAppStore)) {
            return state;
          }
          targetAppStore = {
            ...targetAppStore,
            filter
          };
          return {
            ...state,
            appStores: {
              ...state.appStores,
              [appStoreId]: targetAppStore
            }
          };
        }),
      setAppStorePostsSort: (appStoreId, sortOption) =>
        set((state) => {
          let targetAppStore = state.appStores[appStoreId];
          if (isFalsy(targetAppStore)) {
            return state;
          }
          targetAppStore = {
            ...targetAppStore,
            sortOption
          };
          return {
            ...state,
            appStores: {
              ...state.appStores,
              [appStoreId]: targetAppStore
            }
          };
        }),
      setAppStorePostConditions: (appStoreId, filter, sortOption) =>
        set((state) => {
          let targetAppStore = state.appStores[appStoreId];
          if (isFalsy(targetAppStore)) {
            return state;
          }

          targetAppStore = {
            ...targetAppStore,
            filter,
            sortOption
          };
          return {
            ...state,
            appStores: {
              ...state.appStores,
              [appStoreId]: targetAppStore
            }
          };
        }),
      updatePostStatisitcs: (appStoreId, statisticsKey, data) =>
        set((state) => {
          const targetAppStore = state.appStores[appStoreId];
          if (!targetAppStore) {
            return state;
          } else {
            const newAppStore = {
              ...targetAppStore,
              postData: {
                ...(targetAppStore.postData ?? {}),
                posts: targetAppStore.postData?.posts ?? [],
                pageData: targetAppStore.postData?.pageData ?? {
                  pageNo: 1,
                  pageSize: POST_PAGGE_SIZE
                },
                statistics: {
                  ...(targetAppStore?.postData?.statistics ?? {}),
                  [statisticsKey]: data
                }
              }
            };
            return {
              ...state,
              appStores: {
                ...state.appStores,
                [appStoreId]: newAppStore
              }
            };
          }
        }),
      pushOnCrawlingAppStoreId: (appStoreId) =>
        set((state) => ({
          ...state,
          onCrawlingAppStoreIds: [...state.onCrawlingAppStoreIds, appStoreId]
        })),
      popOnCrawlingAppStoreId: (appStoreId) =>
        set((state) => ({
          ...state,
          onCrawlingAppStoreIds: state.onCrawlingAppStoreIds.filter((id) => id !== appStoreId)
        })),
      clearTempAppStore: () => set((state) => ({ ...state, tempAppStore: null }))
    }),
    { name: 'appStoreStore', storage: createJSONStorage(() => localStorage) }
  )
);
